import { Box } from '@mui/material';
import { z } from 'zod';

import Heading from '@/features/Dashboard/components/Heading';
import { SPOT_CHART_MAX_ITEMS } from '@/features/Dashboard/constants/const';
import { DrawerStep } from '@/features/Dashboard/drawer/types';
import ColorPaletteSelector from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector';
import { ColorPalette } from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import { ItemSelection } from '@/features/Dashboard/form/AppForm/components/ItemSelection';
import { SelectChartType } from '@/features/Dashboard/form/AppForm/components/SelectChartType';
import { TabName } from '@/features/Dashboard/form/AppForm/components/TabName';
import type { ChartFormValues } from '@/features/Dashboard/form/AppForm/context/types';
import type { SimpleChartWidgetV2 } from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';

import type { FormAppDefinition } from '../types';
import {
  buildChart,
  ColorPaletteSchema,
  graphToFormValues,
  NameSchema,
  SymbolsStateSchema
} from './_helpers';

export const spotChartDefinition: FormAppDefinition<ChartFormValues, SimpleChartWidgetV2> = {
  type: AppType.Spot,
  drawerStep: DrawerStep.SelectSpotChart,
  dashboardControls: { edit: true },
  widgetControls: { export: true },
  maxItems: SPOT_CHART_MAX_ITEMS,
  initialValues: {
    name: 'Spot History chart',
    type: AppType.Spot,
    symbols: { active: [], backlog: [] },
    colorPalette: ColorPalette.Vibrant
  },
  schema: z.object({
    id: z.string().optional(),
    type: z.literal(AppType.Spot),
    name: NameSchema,
    symbols: SymbolsStateSchema,
    colorPalette: ColorPaletteSchema,
    from: z.string().optional()
  }),
  Form: () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Heading
        subtitle='Set up your chart preferences and data'
        title='Configure spot chart'
      />
      <TabName />
      <SelectChartType />
      <ItemSelection />
      <ColorPaletteSelector />
    </Box>
  ),
  toWidget: values => buildChart(values),
  canEditWidget: (w): w is SimpleChartWidgetV2 => w.type === 'graph' && !w.metadata?.graphType,
  fromWidget: w => ({ ...graphToFormValues(w), type: AppType.Spot })
};
